// Library Methods
import {
  createContext,
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";

// Contexts
import { useClinicContext } from "../clinic/ClinicProvider";

// Hooks
import { useClinicById } from "src/services/clinics/hooks/useClinicById";
import { useUpdateClinicSettings } from "src/services/clinics/hooks/clinic-settings/useUpdateClinicSettings";

// Types
import { UpdateClinicSettingsType } from "src/services/clinics/types";

// Constants
import {
  DEFAULT_CURRENCY,
  DEFAULT_LANGUAGE_SETTING,
  DEFAULT_PDF_LAYOUT_SETTING,
  DEFAULT_REPORT_TYPE,
  ReportFileType,
} from "src/constants/enums";

// Utils
import { getLanguageCode } from "src/utils/locale/localeHelper";

const ClinicSettingsContext = createContext({
  currentSettings: {} as UpdateClinicSettingsType,
  isLoading: false,
  isUpdating: false,
  handleUpdateSettings: (_: UpdateClinicSettingsType) => {},
});

export const useClinicSettingsContext = () => {
  const context = useContext(ClinicSettingsContext);
  if (!context) {
    throw new Error(
      "useClinicSettingsContext must be used within ClinicSettingsContextProvider"
    );
  }

  return context;
};

type ClinicSettingsProviderType = {
  children: React.ReactNode;
};

// A context that contains logic to update clinic setting values
const ClinicSettingsProvider = ({ children }: ClinicSettingsProviderType) => {
  const { i18n } = useTranslation();
  const { clinicInfo } = useClinicContext();

  const {
    data: clinicData,
    isLoading: isLoadingClinic,
    isError: isErrorClinic,
    isFetching: isFetchingClinic,
    isRefetching: isRefetchingClinic,
  } = useClinicById({ useToken: true, clinicId: clinicInfo?.id });

  const { mutate: editMutate, isPending: isEditPending } =
    useUpdateClinicSettings(clinicInfo?.id);

  const isLoading = useMemo(
    () => isLoadingClinic || isErrorClinic,
    [isLoadingClinic, isErrorClinic]
  );

  const isUpdating = useMemo(
    () => isEditPending || isFetchingClinic || isRefetchingClinic,
    [isEditPending, isFetchingClinic, isRefetchingClinic]
  );

  const [currentSettings, setCurrentSettings] =
    useState<UpdateClinicSettingsType>({
      id: "",

      // sound settings
      enableVRExamCompleteNotificationSounds: false,
      enablePortalSounds: false,

      // language settings
      clinicDefaultLanguage: DEFAULT_LANGUAGE_SETTING,
      patientDefaultLanguage: DEFAULT_LANGUAGE_SETTING,

      // currency settings
      currencySymbol: DEFAULT_CURRENCY.split(" - ")[0],
      currencyCode: DEFAULT_CURRENCY.split(" - ")[1],

      // layout settings
      defaultReportType: DEFAULT_REPORT_TYPE,
      monocularReportPdfLayoutOption: DEFAULT_PDF_LAYOUT_SETTING,
      reportDisplayLeftEyeFirst: false,
      MTOFixationMethod: false,

      // exam settings
      colorVisionDefaultTimeLimit: -1,
    });

  // initialize settings after the clinic is loaded
  // re-initialize settings if the clinic is changed
  useEffect(() => {
    if (
      clinicData?.settings &&
      clinicData?.settings?.id !== currentSettings.id
    ) {
      setCurrentSettings({
        id: clinicData.settings.id,

        // sound settings
        enableVRExamCompleteNotificationSounds:
          clinicData.settings.enableVRExamCompleteNotificationSounds,
        enablePortalSounds: clinicData.settings.enablePortalSounds,

        // language settings
        clinicDefaultLanguage:
          clinicData.settings.clinicDefaultLanguage ?? DEFAULT_LANGUAGE_SETTING,
        patientDefaultLanguage:
          clinicData.settings.patientDefaultLanguage ??
          DEFAULT_LANGUAGE_SETTING,

        // currency settings
        currencySymbol:
          clinicData.settings.currencySymbol ??
          DEFAULT_CURRENCY.split(" - ")[0],
        currencyCode:
          clinicData.settings.currencyCode ?? DEFAULT_CURRENCY.split(" - ")[1],

        // layout settings
        defaultReportType:
          clinicData.settings.defaultReportType ?? DEFAULT_REPORT_TYPE,
        monocularReportPdfLayoutOption:
          clinicData.settings.monocularReportPdfLayoutOption ??
          DEFAULT_PDF_LAYOUT_SETTING,
        reportDisplayLeftEyeFirst:
          clinicData.settings.reportDisplayLeftEyeFirst,
        MTOFixationMethod: clinicData.settings.MTOFixationMethod,

        // exam settings
        colorVisionDefaultTimeLimit:
          clinicData.settings.colorVisionDefaultTimeLimit / 60.0,
      });
    }
  }, [clinicData, currentSettings]);

  // Only block the save on a field being actively changed to an invalid value —
  // an unrelated field elsewhere still holding its unloaded/default value
  // shouldn't stop this update from going through.
  const disableAutosave = useCallback(
    (updatedSettings: UpdateClinicSettingsType) => {
      const isChanging = (key: keyof UpdateClinicSettingsType) =>
        updatedSettings[key] !== currentSettings[key];

      return (
        (isChanging("clinicDefaultLanguage") &&
          !updatedSettings.clinicDefaultLanguage) ||
        (isChanging("patientDefaultLanguage") &&
          !updatedSettings.patientDefaultLanguage) ||
        (isChanging("monocularReportPdfLayoutOption") &&
          !updatedSettings.monocularReportPdfLayoutOption) ||
        (isChanging("colorVisionDefaultTimeLimit") &&
          updatedSettings.colorVisionDefaultTimeLimit < 0)
      );
    },
    [currentSettings]
  );

  const handleUpdateSettings = useCallback(
    (updatedSettings: UpdateClinicSettingsType) => {
      if (disableAutosave(updatedSettings)) return;

      const newClinicSettings = {
        ...updatedSettings,

        // language settings
        clinicDefaultLanguage: updatedSettings?.clinicDefaultLanguage,
        patientDefaultLanguage: updatedSettings?.patientDefaultLanguage,

        // layout settings
        defaultReportType: updatedSettings?.defaultReportType as ReportFileType,
        monocularReportPdfLayoutOption:
          updatedSettings?.monocularReportPdfLayoutOption,
        reportDisplayLeftEyeFirst: updatedSettings?.reportDisplayLeftEyeFirst,
        MTOFixationMethod: updatedSettings?.MTOFixationMethod,

        // exam settings
        colorVisionDefaultTimeLimit:
          updatedSettings?.colorVisionDefaultTimeLimit * 60.0,
      };

      editMutate(newClinicSettings, {
        onSuccess: () => {
          setCurrentSettings((prev) => ({
            ...prev,
            ...updatedSettings,
          }));

          // Only change the language if it is actually different
          // Otherwise all of the t() calls have to update, which causes lag
          const languageCode = getLanguageCode(
            newClinicSettings?.clinicDefaultLanguage
          );
          if (languageCode !== i18n.language) {
            i18n.changeLanguage(languageCode);
          }
        },
      });
    },
    [i18n, disableAutosave, editMutate]
  );

  return (
    <ClinicSettingsContext.Provider
      value={{ currentSettings, isLoading, isUpdating, handleUpdateSettings }}
    >
      {children}
    </ClinicSettingsContext.Provider>
  );
};

export default ClinicSettingsProvider;
